import { Controller } from "@hotwired/stimulus";
import axios from "axios";
import PaymentModalService from "../../service/payment/payment_modal_service";
import paymentMiddlewareUrlService from "../../service/payment/payment_middleware_url_service";

import { trackBarcodeEvent } from "../../service/analytics/barcodeAnalytics";

export default class extends Controller {
  static targets = ["option", "billing", "price", "period", "billingNote", "saving", "savingLine", "savingPercent", "checkout", "currencyButton", "selectedCurrency", "currencyList"];

  connect() {
    this.currencyCode = "usd";
    this.paypalAllowed = true;
    this.upiAllowed = false;
    this.showAnnual();
    this.loadPrices();
    this.pricingObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        trackBarcodeEvent("barcode_plan_view");
        this.pricingObserver.disconnect();
      }
    });
    this.pricingObserver.observe(this.element);
  }

  disconnect() {
    this.pricingObserver?.disconnect();
  }

  async loadPrices() {
    try {
      const [{ data: currencies }, { data: plans }] = await Promise.all([
        axios("/get-all-currencies", { params: { pageFilter: "HOME_PAGE" } }),
        axios.post("/get-all-plans", {}),
      ]);

      if (!this.element.isConnected) {
        return;
      }

      this.plans = plans;
      this.currencies = currencies.filter((currency) =>
        ["starter", "business", "unlimited"].every((tier) =>
          ["month", "year"].every((period) =>
            plans[`barcode_${tier}_${period}`]?.some((plan) =>
              plan.currency === currency.code && Number.isFinite(Number(plan.amount))
            )
          )
        )
      );
      this.buildCurrencyDropdown();
      if (this.currencies.length > 0) {
        this.setCurrency(this.currencies[0]);
      }
    } catch (error) {
      console.error("Failed to load barcode plan prices", error);
    }
  }

  buildCurrencyDropdown() {
    this.currencyListTarget.replaceChildren();
    this.currencies.forEach((currency) => {
      const item = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "dropdown-item cursor-pointer";
      button.textContent = currency.code.toUpperCase();
      button.dataset.currency = currency.code;
      button.dataset.action = "click->barcode--pricing#changeCurrency";
      item.appendChild(button);
      this.currencyListTarget.appendChild(item);
    });
    this.currencyButtonTarget.disabled = this.currencies.length === 0;
  }

  changeCurrency(event) {
    const currency = this.currencies.find((item) => item.code === event.currentTarget.dataset.currency);
    if (currency) {
      this.setCurrency(currency);
    }
  }

  setCurrency(currency) {
    this.currencyCode = currency.code;
    this.paypalAllowed = currency.isAllowedForPayPal ?? true;
    this.upiAllowed = currency.isAllowedForUpi ?? false;
    this.selectedCurrencyTarget.textContent = currency.code.toUpperCase();
    const formatPrice = (amount) => `${currency.sign}${Number(amount.toFixed(2))}`;

    let maxSavingPercent = 0;
    ["starter", "business", "unlimited"].forEach((tier) => {
      const monthlyPlan = this.plans[`barcode_${tier}_month`]?.find((plan) => plan.currency === currency.code);
      const annualPlan = this.plans[`barcode_${tier}_year`]?.find((plan) => plan.currency === currency.code);
      const monthly = Number(monthlyPlan?.amount);
      const annual = Number(annualPlan?.amount);

      if (!monthlyPlan || !annualPlan || !Number.isFinite(monthly) || !Number.isFinite(annual)) {
        return;
      }

      this.priceTargets.filter((target) => target.dataset.tier === tier).forEach((target) => {
        target.dataset.monthly = formatPrice(monthly);
        target.dataset.annual = formatPrice(annual / 12);
      });

      const savingAmount = Number((monthly * 12 - annual).toFixed(2));
      if (monthly > 0) {
        maxSavingPercent = Math.max(maxSavingPercent, (1 - annual / (monthly * 12)) * 100);
      }
      this.savingTargets.filter((target) => target.dataset.tier === tier).forEach((target) => {
        target.textContent = formatPrice(savingAmount);
        target.dataset.hasSaving = String(savingAmount > 0);
      });
    });

    const savingPercent = Math.floor(Number(maxSavingPercent.toFixed(8)));
    this.savingPercentTargets.forEach((target) => {
      target.textContent = String(savingPercent);
    });

    this.priceTargets.filter((target) => !target.dataset.tier).forEach((target) => {
      target.dataset.monthly = formatPrice(0);
      target.dataset.annual = formatPrice(0);
    });
    this.setMode(this.mode);
  }

  showMonthly() {
    this.setMode("monthly");
  }

  showAnnual() {
    this.setMode("annual");
  }

  selectPlan(event) {
    event.preventDefault();
    const subscriptionType = this.getSubscriptionType(event.currentTarget.dataset.tier);

    const plan = subscriptionType.replace(/^barcode_/, "");
    trackBarcodeEvent("barcode_upgrade_click", { plan });
    trackBarcodeEvent("barcode_plan_select", { plan });

    PaymentModalService.setChoosePaymentMethodActions({
      cardBtnActiion: "checkoutBarcodeCard",
      paypalBtnAction: "checkoutBarcodePaypal",
      subscriptionType,
      currency: this.currencyCode,
      isAllowedForPayPal: this.paypalAllowed,
      isAllowedForUpi: this.upiAllowed,
    });
  }

  getSubscriptionType(tier) {
    return `barcode_${tier}_${this.mode === "annual" ? "year" : "month"}`;
  }

  setMode(mode) {
    this.mode = mode;
    this.optionTargets.forEach((option) => {
      const isActive = option.dataset.barcodePricingModeParam === mode;

      option.classList.toggle("btn-secondary", isActive);
      option.classList.toggle("btn-outline-secondary", !isActive);
      option.setAttribute("aria-pressed", String(isActive));
    });

    this.billingTargets.forEach((billing) => {
      billing.textContent = billing.dataset[mode];
    });

    this.priceTargets.forEach((price) => {
      price.textContent = price.dataset[mode] ?? "";
    });

    this.periodTargets.forEach((period) => {
      const price = period.previousElementSibling;
      period.textContent = price?.dataset[mode] ? period.dataset[`${mode}Period`] || "" : "";
    });

    this.billingNoteTargets.forEach((billingNote) => {
      billingNote.textContent = billingNote.dataset[mode];
    });

    this.savingLineTargets.forEach((line) => {
      const saving = line.querySelector('[data-barcode--pricing-target="saving"]');
      const hidden = mode !== "annual" || saving?.dataset.hasSaving !== "true";
      line.classList.toggle("d-none", hidden);
      line.closest(".d-flex").querySelector('[data-barcode--pricing-target="savingIcon"]')?.classList.toggle("d-none", hidden);
    });

    this.checkoutTargets.forEach((checkout) => {
      checkout.href = paymentMiddlewareUrlService.getBarcodeMiddlewareUrl({
        subscriptionType: this.getSubscriptionType(checkout.dataset.tier),
        currency: this.currencyCode,
      });
    });
  }
}
